<script setup lang="ts">
import type { PropType } from 'vue';

import { storeToRefs } from 'pinia';
import { computed, nextTick, onMounted, onUnmounted, ref, useId, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import type { Cart } from '@/components/cart/cart.type';

import { useGlobalConfigurationStore } from '@/stores/useGlobalConfigurationStore';

const props = defineProps({
  cart: {
    type: Object as PropType<Cart>,
    required: true,
  },
  startOpen: {
    type: Boolean,
    default: false,
  },
  apiKey: {
    type: String,
    required: true,
  },
});

const { t } = useI18n();
const { apiURL } = storeToRefs(useGlobalConfigurationStore());
const isOpen = ref(props.startOpen);
const contentRef = useTemplateRef('contentRef');
const cartWidgetUrl = computed(() => apiURL.value.replace('/v3', '/static/widgets/cart'));
const contentHeight = ref('0px');
const componentId = useId();
const contentId = computed(() => `accordion-content-${componentId}`);

function toggleAccordion() {
  isOpen.value = !isOpen.value;
};

const logoAssetsUrl = ref<string>(window.location.hostname === 'localhost' ? 'http://localhost:5173/public/' : 'https://components.youbook.travel');
const iFrameProviderWidgetHeight = ref<number>(780);

function resizeIframe(event: any) {
  if (event.data?.type === 'widget-resize') {
    iFrameProviderWidgetHeight.value = event.data.widgetHeight + 40;
  }
}

function getProviderLogo(source: string) {
  return `${logoAssetsUrl.value}/${source}.png`;
}

function setupWidgetHeight() {
  const height = contentRef.value?.getBoundingClientRect().height;
  contentRef.value?.firstElementChild?.setAttribute('height', `${Math.max(height ?? 450, 450)}px`);
}

watch(isOpen, async (newValue) => {
  await nextTick();
  if (newValue) {
    contentHeight.value = `${iFrameProviderWidgetHeight.value.toString()}px`;
  }
  else {
    // Ensure we transition from the measured height down to 0
    contentHeight.value = `${iFrameProviderWidgetHeight.value.toString()}px`;
    requestAnimationFrame(() => {
      contentHeight.value = '0px';
    });
  }
});

onMounted(async () => {
  if (props.startOpen && contentRef.value) {
    contentHeight.value = `${iFrameProviderWidgetHeight.value.toString()}px`;
  }
  window.addEventListener('message', resizeIframe);
  const iframeSrc = `${cartWidgetUrl.value}?apiKey=${props.apiKey}&provider=${props.cart.provider}`;
  contentRef.value?.firstElementChild?.setAttribute('src', iframeSrc);
  await nextTick();
  contentRef.value?.firstElementChild?.setAttribute('width', '100%');
  (contentRef.value?.firstElementChild as HTMLElement).style.border = 'none';
  setupWidgetHeight();
});

onUnmounted(() => window.removeEventListener('message', resizeIframe));
</script>

<template>
  <div class="accordion-item">
    <div class="accordion-header" :class="[isOpen ? 'open' : '']">
      <p v-if="!isOpen" class="header-text-closed">
        {{ t('cart.disclaimer') }}
      </p>
      <div class="header-controls">
        <img
          v-if="getProviderLogo(cart.provider)" :src="getProviderLogo(cart.provider)" alt="provider logo"
          width="100px" class="header-image"
        >
        <button
          type="button"
          class="toggle-button"
          :class="[isOpen ? 'open' : '']"
          :aria-expanded="isOpen"
          :aria-controls="contentId"
          @click="toggleAccordion"
        >
          <span>{{ t('cart.article', { count: cart.count }) }}</span>
          <svg
            class="chevron-icon"
            :class="{ 'is-open': isOpen }"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
      </div>
    </div>
    <div
      :id="contentId"
      ref="contentRef"
      class="accordion-content-wrapper"
      :style="{ maxHeight: contentHeight }"
    >
      <iframe title="cart" @load="setupWidgetHeight" />
    </div>
  </div>
</template>

<style scoped>
.accordion-item {
  min-width: 96%;
  background-color: var(--yb-color-white);
  border: 1px solid hsl(0, 0%, 78%);
  padding-inline: 0.5rem;
  border-radius: var(--yb-card-radius);
  overflow: hidden;
}

.accordion-header {
  padding-block: 1rem;
  cursor: pointer;
  background-color: var(--yb-color-white);

  &.open {
    padding-block: 1.5rem;
    border-bottom: 1px solid hsl(0, 0%, 78%);
  }
}

.header-text-closed {
  text-align: left;
  font-size: var(--yb-text-xs);
  font-style: italic;
  margin-bottom: var(--yb-spacing-sm);
}

.header-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header-image {
  flex-shrink: 0; /* Prevent image from shrinking */
}

.toggle-button {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  background: var(--yb-color-black);
  border: none;
  padding: 1rem;
  margin-left: auto;
  font-size: var(--yb-text-base);
  font-weight: var(--yb-text-bold);
  color: var(--yb-color-white);
  cursor: pointer;
  text-align: right;
  transition: all 0.2s ease-out;

  &.open {
    background: var(--yb-color-white);
    color: var(--yb-color-black);
    padding: 0.25rem;
  }
}

.toggle-button span {
  margin-right: 0.5rem;
}

.chevron-icon {
  width: 18px;
  height: 18px;
  transition: transform 0.3s ease-in-out;
  flex-shrink: 0; /* Prevent icon from shrinking */
}

.chevron-icon.is-open {
  transform: rotate(180deg);
}

.accordion-content-wrapper {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.35s ease-in-out;
}

.accordion-content {
  padding-block: 1rem;
  background-color: var(--yb-color-white);
}
</style>
